// TODO: Replace remaining TextInput with form field components from @/components/form
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useCreateFolder } from '@/api/community-essentials/create-folder'
import { useGetAllFolders } from '@/api/community-essentials/get-all-folders'
import { useUpdateFolder } from '@/api/community-essentials/update-folder'
import { SelectFormField } from '@/components/form/select-form-field'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { TextInput } from '@/components/ui/text-input'
import { toastFactory } from '@/factories'
import type { CommunityEssentialFolderModel } from '@/models'
import { getApiErrorMessage } from '@/utils/error'

import { FolderFormData, makeFolderFormSchema } from '../community-essentials.constants'
import { buildFolderTreeOptions } from '../community-essentials.utils'
import * as S from './folder-form-modal.styled'

interface FolderFormBaseProps {
  buildingId: number
  onClose: () => void
  onSuccess?: (name: string) => void
}

interface FolderFormCreateProps extends FolderFormBaseProps {
  mode: 'create'
  parentFolderId: number | null
}

interface FolderFormEditProps extends FolderFormBaseProps {
  folder: CommunityEssentialFolderModel
  mode: 'edit'
}

type FolderFormProps = FolderFormCreateProps | FolderFormEditProps

export const FolderFormModal = (props: FolderFormProps) => {
  const { t } = useTranslation(['community-essentials', 'common'])
  const { buildingId, mode, onClose, onSuccess } = props
  const folder = mode === 'edit' ? props.folder : null

  const { mutateAsync: createFolderMutation, isPending: isCreating } = useCreateFolder()
  const { mutateAsync: updateFolderMutation, isPending: isUpdating } = useUpdateFolder()

  const { data: allFolders } = useGetAllFolders({ buildingId })

  const defaultParentId =
    mode === 'edit' ? (props.folder.parentFolderId ?? 0) : (props.parentFolderId ?? 0)

  const form = useForm<FolderFormData>({
    defaultValues: {
      name: folder?.name ?? t('community-essentials:folderFormModal.untitledFolderDefault'),
      targetFolderId: defaultParentId,
    },
    resolver: zodResolver(makeFolderFormSchema(t)),
  })

  const {
    formState: { errors, touchedFields },
    register,
  } = form

  const folderOptions = useMemo(
    () =>
      buildFolderTreeOptions({
        allFolders,
        checkMaxDepth: true,
        editingFolderId: folder?.id,
        t,
      }),
    [allFolders, folder?.id, t],
  )

  const handleSubmit = async (values: FolderFormData) => {
    try {
      const selectedParentId = values.targetFolderId === 0 ? null : values.targetFolderId
      if (mode === 'edit') {
        await updateFolderMutation({
          folderId: props.folder.id,
          name: values.name,
          parentFolderId: selectedParentId,
        })
      } else {
        await createFolderMutation({
          buildingId,
          name: values.name,
          parentFolderId: selectedParentId,
        })
      }
      onSuccess?.(values.name)
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error as Error))
    }
  }

  return (
    <FormProvider {...form}>
      <Modal
        size="small"
        title={
          mode === 'edit'
            ? t('community-essentials:folderFormModal.editTitle')
            : t('community-essentials:folderFormModal.addTitle')
        }
        footer={
          <>
            <Button use="secondary" size="medium" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              loading={isCreating || isUpdating}
              size="medium"
              onClick={form.handleSubmit(handleSubmit)}
            >
              {mode === 'edit'
                ? t('community-essentials:update')
                : t('community-essentials:folderFormModal.submitCreateButton')}
            </Button>
          </>
        }
        onClose={onClose}
      >
        <S.Fields>
          <TextInput
            error={touchedFields.name && errors.name ? errors.name.message : undefined}
            label={t('community-essentials:folderFormModal.name.label')}
            placeholder={t('community-essentials:folderFormModal.name.placeholder')}
            {...register('name')}
          />

          <SelectFormField
            searchable
            type="nested"
            label={t('community-essentials:folderFormModal.location.label')}
            name="targetFolderId"
            options={folderOptions}
            placeholder={t('community-essentials:folderFormModal.location.placeholder')}
          />
        </S.Fields>
      </Modal>
    </FormProvider>
  )
}
